import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { isClientPortalUser, isPlatformAdmin } from '../../utils/authUtils';
import { getClientPortalDashboardPath } from '../../utils/portalAccess';
import { isFundGeniusPlatformClientsRoute } from '../../utils/fundgeniusWorkspaceRouting';

export function FundGeniusStatusScreen({
  message,
  detail,
  tone = 'dark',
  showRetry = false,
  onRetry,
  actions,
  testId = 'fundgenius-workspace-status',
}) {
  const isDark = tone === 'dark';
  return (
    <div
      className={`flex min-h-[60vh] flex-col items-center justify-center px-6 text-center ${
        isDark ? 'text-slate-100' : 'text-slate-800'
      }`}
      data-testid={testId}
    >
      <Loader2 className={`mb-4 size-10 animate-spin ${isDark ? 'text-cyan-400' : 'text-emerald-600'}`} />
      <p className="text-lg font-medium">{message}</p>
      {detail ? <p className="mt-2 max-w-md text-sm opacity-80">{detail}</p> : null}
      {showRetry ? (
        <Button
          type="button"
          variant="outline"
          className="mt-6 rounded-xl"
          onClick={onRetry || (() => window.location.reload())}
          data-testid="fundgenius-workspace-retry"
        >
          <RefreshCw className="mr-2 size-4" />
          Retry
        </Button>
      ) : null}
      {actions}
    </div>
  );
}

export function FundGeniusWorkspaceErrorCard({
  title = 'We could not load your FundGenius workspace.',
  detail,
  onRetry,
  onSignOut,
}) {
  return (
    <div
      className="mx-auto max-w-lg rounded-2xl border border-rose-400/30 bg-rose-950/40 px-6 py-8 text-center text-slate-100"
      data-testid="fundgenius-workspace-error"
    >
      <AlertTriangle className="mx-auto mb-4 size-10 text-rose-300" />
      <h2 className="text-lg font-semibold">{title}</h2>
      {detail ? <p className="mt-2 text-sm text-slate-300">{detail}</p> : null}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Button type="button" className="rounded-xl" onClick={onRetry || (() => window.location.reload())}>
          Retry
        </Button>
        {onSignOut ? (
          <Button type="button" variant="outline" className="rounded-xl" onClick={onSignOut}>
            Sign out
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Ensures FundGenius HQ never renders a blank shell while auth/workspace resolves.
 */
export function FundGeniusWorkspaceGate({ children }) {
  const location = useLocation();
  const { user, loading: authLoading, logout } = useAuth();
  const {
    workspaceAgencyId,
    isPlatformContext,
    workspaceError,
    workspaceLoading,
  } = useFundingWorkspace();

  if (authLoading || workspaceLoading) {
    return (
      <FundGeniusStatusScreen message="Loading your FundGenius workspace…" />
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isClientPortalUser(user)) {
    return <Navigate to={getClientPortalDashboardPath(user)} replace />;
  }

  if (
    isFundGeniusPlatformClientsRoute(location.pathname)
    && !isPlatformAdmin(user)
  ) {
    return <Navigate to="/fundgenius-hq/clients" replace />;
  }

  if (workspaceError) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <FundGeniusWorkspaceErrorCard
          detail={workspaceError}
          onSignOut={() => logout()}
        />
      </div>
    );
  }

  if (!isPlatformContext && !workspaceAgencyId && !isPlatformAdmin(user)) {
    return (
      <FundGeniusStatusScreen
        message="Preparing your FundGenius workspace…"
        detail="Your agency workspace is still being set up. This usually takes a few seconds after signup."
        showRetry
        testId="fundgenius-workspace-preparing"
        actions={(
          <p className="mt-6 text-sm opacity-70">
            Need help?{' '}
            <Link to="/login" className="text-cyan-300 underline">
              Sign in again
            </Link>
          </p>
        )}
      />
    );
  }

  if (
    !isPlatformAdmin(user)
    && !isPlatformContext
    && !isClientPortalUser(user)
    && user?.agency_id
    && user.agency_id === 'default_agency'
  ) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <FundGeniusWorkspaceErrorCard
          title="Your account does not have access to this FundGenius workspace."
          detail="No agency is linked to your account yet. Contact support if you just completed signup."
          onSignOut={() => logout()}
        />
      </div>
    );
  }

  return children;
}

export default FundGeniusWorkspaceGate;
